import { fetchFragmentArea } from '@gialaboratory/sdk/report-check-fragments';
import { useQuery } from '@tanstack/react-query';
import React, { useContext, useEffect } from 'react';
import { FragmentRenderer } from '@gialaboratory/sdk/fragment-renderer';
import { TLocale } from '@@config/locale/';
import { LocaleContext, TLocaleContext } from '@@contexts/LocaleContext';
import { getMagnoliaRestBaseURL } from '@@utils/utils';
import {
    getCookiePreferencesHash,
    isCookiePreferencesHash,
    openTrustArcCookiePreferencesWithRetry,
} from '@@utils/trustarc-preferences';

const FRAGMENT_PAGE_NAME = 'footer' as const;

const fetchFooterContent = async (locale: TLocale) => {
    const baseUrl = getMagnoliaRestBaseURL();
    try {
        return await fetchFragmentArea(FRAGMENT_PAGE_NAME, locale, { baseUrl });
    } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        console.error(
            '[Footer] Fragment fetch failed:',
            msg,
            'url:',
            `${baseUrl}/.rest/api/report-check/fragments/${FRAGMENT_PAGE_NAME}/${locale}`
        );
        throw err;
    }
};

const Footer: React.FC = () => {
    const { locale }: TLocaleContext = useContext(LocaleContext);

    useEffect(() => {
        const targetHash = getCookiePreferencesHash();

        const openIfTargetHash = () => {
            if (isCookiePreferencesHash(window.location.hash)) {
                void openTrustArcCookiePreferencesWithRetry({ attempts: 5, delayMs: 200 });
            }
        };

        const onClick = (event: MouseEvent) => {
            const target = event.target;
            if (!(target instanceof Element)) return;

            const link = target.closest('a[href]') as HTMLAnchorElement | null;
            if (!link) return;

            try {
                const url = new URL(link.href, window.location.href);
                if (url.hash !== targetHash) return;
            } catch {
                if (!link.getAttribute('href')?.includes(targetHash)) return;
            }

            event.preventDefault();
            void openTrustArcCookiePreferencesWithRetry({ attempts: 5, delayMs: 200 });
        };

        openIfTargetHash();
        window.addEventListener('hashchange', openIfTargetHash);
        document.addEventListener('click', onClick);

        return () => {
            window.removeEventListener('hashchange', openIfTargetHash);
            document.removeEventListener('click', onClick);
        };
    }, []);

    const {
        data: content,
        isLoading,
        error,
    } = useQuery({
        queryKey: ['reportCheckFragment', FRAGMENT_PAGE_NAME, locale],
        queryFn: () => fetchFooterContent(locale),
        staleTime: 15 * 60 * 1000, // 15 minutes
    });

    if (error) {
        const msg = error instanceof Error ? error.message : String(error);
        console.error('[Footer] Error loading footer content:', msg);
        return (
            <footer className='footer m-0 bg-surface-dark pb-[20px] pt-[30px] text-white' role='alert'>
                <p className='text-center'>
                    Footer content is not available. Ensure the report-check API is running at{' '}
                    {getMagnoliaRestBaseURL()}.
                </p>
            </footer>
        );
    }

    const areaContent = content?.footer;

    return (
        <>
            {!isLoading && areaContent != null ? (
                <FragmentRenderer
                    className='gia-fragment-content'
                    content={areaContent as Record<string, unknown>}
                    locale={locale}
                    localeUrlStyle='query'
                    magnoliaPublicBaseUrl={getMagnoliaRestBaseURL()}
                />
            ) : null}
        </>
    );
};

export default Footer;
